// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCsvExport } from '@hooks/other/useCsvExport';
import { useDialogControl } from '@hooks/other/useDialogControl';
import React, { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { InfoOutlined, MoreVert } from '@mui/icons-material';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { Holding } from '@daml.js/dtc-registry-holding/lib/Dtc/Registry/Holding/V3/Holding';
import { ChoiceDialog } from '../../../components/ChoiceDialog';
import CopyableText from '../../../components/CopyableText';
import { HoldingDetail } from '../../../components/HoldingDetails';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import useComplianceService from '../../../hooks/queries/registry/onboarding/useComplianceService';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import {
  InstrumentsById,
  useInstrumentConfigurationsById,
} from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import TemplateContract from '../../../utils/TemplateContract';
import { aggregateUnlockedHoldings } from '../../../utils/aggregateHoldings';
import { fmt, formatTimestamp, parseTimestampToDate, values } from '../../../utils/common';
import { PageWrapper } from '../../PageWrapper';
import { ClawbackDialog } from '../clawback/ClawbackDialog';
import { OfferTransferDialog } from '../transfer/OfferTransferDialog';
import { useCreateHoldingsActions } from './useCreateHoldingsActions';

type Row = TemplateContract<Holding>;

const createHeaders: (instrumentsById: InstrumentsById) => GridColDef<Row>[] = (
  instrumentsById,
) => {
  return [
    {
      field: 'name',
      headerName: 'Name',
      valueGetter: (params) => {
        const id = params.row.payload.instrument.id;
        return instrumentsById[id]?.payload.tokenName || id;
      },
      flex: 100,
      filterable: true,
    },
    {
      field: 'instrumentId',
      headerName: 'InstrumentId',
      valueGetter: (params) => params.row.payload.instrument.id,
      renderCell: (params) => <CopyableText value={params.row.payload.instrument.id} />,
      flex: 100,
      filterable: true,
    },
    {
      field: 'symbol',
      headerName: 'Symbol',
      valueGetter: (params) =>
        instrumentsById[params.row.payload.instrument.id]?.payload.tokenSymbol || '',
      flex: 100,
      filterable: true,
    },
    {
      field: 'cusip',
      headerName: 'CUSIP',
      valueGetter: (params) =>
        instrumentsById[params.row.payload.instrument.id]?.payload.cusip || '',
      flex: 100,
      filterable: true,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      valueGetter: (params) => Number(params.row.payload.amount),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Box>{fmt(params.row.payload.amount, 2)} </Box>
          {params.row.payload.lock && <LockIcon fontSize="small" sx={{ scale: 0.5 }} />}
        </Box>
      ),
      flex: 100,
      filterable: true,
    },
    {
      field: 'holder',
      headerName: 'Holder',
      renderCell: (params) => <PartyDisplay party={params.row.payload.owner} />,
      flex: 150,
      valueGetter: (params) => params.row.payload.owner,
      filterable: true,
    },
    {
      field: 'participant',
      headerName: 'Participant',
      valueGetter: (params) => params.row.payload.participant,
      renderCell: (params) =>
        params.row.payload.participant ? (
          <PartyDisplay party={params.row.payload.participant} />
        ) : (
          ''
        ),
      flex: 150,
      filterable: true,
    },
    {
      field: 'lockedBy',
      headerName: 'Locked By',
      valueGetter: (params) =>
        params.row.payload.lock ? values(params.row.payload.lock.lockers).join(', ') : '',
      renderCell: (params) =>
        params.row.payload.lock
          ? values(params.row.payload.lock.lockers).map((party) => (
              <PartyDisplay key={uuidv4()} party={party} />
            ))
          : null,
      flex: 150,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      type: 'dateTime',
      valueGetter: (params) => parseTimestampToDate(params.row.createdAt),
      renderCell: (params) => formatTimestamp(params.row.createdAt),
      flex: 150,
      minWidth: 180,
    },
    {
      field: 'action',
      headerName: 'Actions',
      align: 'right',
      renderCell: () => (
        <Box sx={{ cursor: 'pointer', display: 'flex' }}>
          <MoreVert />
        </Box>
      ),
      flex: 150,
      filterable: false,
    },
  ];
};

interface HoldingsProps {
  disableVirtualization?: boolean;
}

export const Holdings: React.FC<HoldingsProps> = ({ disableVirtualization }) => {
  const holdings = useHoldings();
  const complianceService = useComplianceService();
  const canClawback = !!complianceService.data;
  const instrumentsById = useInstrumentConfigurationsById();
  const mergedHoldings = React.useMemo(
    () => aggregateUnlockedHoldings(holdings.data || []),
    [holdings.data],
  );
  const [selectedRow, setSelectedRow] = useState<TemplateContract<Holding> | undefined>(undefined);
  const onRowClick = (r: GridRowParams<TemplateContract<Holding>>) => {
    setSelectedRow(r.row);
  };
  const onClose = () => {
    setSelectedRow(undefined);
  };
  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();
  const actions = useCreateHoldingsActions(onClose, selectedRow);
  const columns = useMemo(() => createHeaders(instrumentsById), [instrumentsById]);
  const { handleExport, isExportDisabled } = useCsvExport(columns, mergedHoldings, {
    filename: 'holdings',
  });
  const { isLoading } = holdings;
  if (isLoading) return <Loading />;
  const buttons = [
    {
      name: 'Export CSV',
      onClick: handleExport,
      disabled: isExportDisabled,
    },
    ...(canClawback
      ? [
          {
            name: 'Clawback',
            onClick: () => setDialogName('clawback'),
          },
        ]
      : []),
    {
      name: 'Offer transfer',
      onClick: () => setDialogName('transfer'),
    },
  ];
  return (
    <PageWrapper
      buttons={buttons}
      pageName={
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1 }}>
          <Typography
            sx={{ textTransform: 'capitalize' }}
            fontWeight="bold"
            color="primary"
            variant="h4"
          >
            Holdings
          </Typography>
          <Tooltip title="Click on a holding row to show actions such as transfer and burn.">
            <InfoOutlined color="secondary" fontSize="small" sx={{ ml: 0.5 }} />
          </Tooltip>
        </Box>
      }
    >
      <ChoiceDialog
        title={
          (selectedRow && instrumentsById[selectedRow.payload.instrument.id]?.payload.tokenName) ||
          selectedRow?.payload.instrument.id ||
          ''
        }
        open={!!selectedRow}
        onClose={onClose}
        actions={actions}
      >
        <HoldingDetail row={selectedRow} />
      </ChoiceDialog>
      {dialogName === 'clawback' && <ClawbackDialog open onClose={onCloseDialog} />}
      {dialogName === 'transfer' && <OfferTransferDialog open onClose={onCloseDialog} />}

      <DataTable
        columns={columns}
        rows={mergedHoldings}
        rowKey={(r) => r.contractId}
        onRowClick={onRowClick}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
